import { Controller } from "@hotwired/stimulus"

// Address entry shared by the CME claim's Verification step (TMS-1721) and the membership
// create-account details (TMS-1656).
//
// - The address is a Google Places search box (through the server, AddressSuggestionsController)
//   or its fields, and a link switches between the two. Switching only hides one or the other, so
//   nothing typed is lost and the form submits the same fields whichever produced them.
// - Picking a suggestion fills the fields; if Google left any of them out, the fields open so the
//   person can finish them. A lookup that fails opens them too.
// - The search looks in the country picked beside it. The country and state follow through the
//   app's own selects, and the state list follows the country through the store-address
//   controller on the same form.
// - State and postal code aren't universal: a state is needed only where the country has a list
//   of them, and a postal code only outside `optionalPostalCodeCountries`, whose postal field
//   reads "Optional" instead of its `data-placeholder`.
//
// Whenever the address changes it says so with `address-entry:change`, whose detail carries
// `complete` (every field the address needs is filled) and `pending` (a picked address is still
// being looked up), so the form can decide whether it may be sent.
export default class extends Controller {
  static targets = ["country", "search", "manual", "toManual", "toSearch", "lineOne", "lineTwo", "city", "postalCode"]

  static values = {
    mode: { type: String, default: "manual" },
    addressUrl: String,
    sessionToken: String,
    stateName: String,
    optionalPostalCodeCountries: Array
  }

  connect() {
    this.pending = false
    this.onAddressPicked = (event) => this.fillFromSuggestion(event)
    this.searchField = this.hasSearchTarget ? this.searchTarget.querySelector("[data-async-typeahead-target='hidden']") : null
    this.searchField?.addEventListener("app-select:change", this.onAddressPicked)
    this.onCountryChange = () => this.countryChanged()
    this.countryField = this.countryInput()
    this.countryField?.addEventListener("app-select:change", this.onCountryChange)
    this.syncPostalCode()
    this.announce()
  }

  disconnect() {
    this.searchField?.removeEventListener("app-select:change", this.onAddressPicked)
    this.countryField?.removeEventListener("app-select:change", this.onCountryChange)
  }

  toggleMode() {
    this.modeValue = this.modeValue === "search" ? "manual" : "search"
  }

  modeValueChanged(mode, previous) {
    const searching = mode === "search"
    if (previous !== undefined) this.carryOver(searching)
    if (this.hasSearchTarget) this.searchTarget.hidden = !searching
    this.manualTargets.forEach((element) => { element.hidden = searching })
    if (this.hasToManualTarget) this.toManualTarget.hidden = !searching
    if (this.hasToSearchTarget) this.toSearchTarget.hidden = searching
    this.announce()
  }

  // Tell the form where the address stands. Run on every edit inside the address too.
  announce() {
    this.dispatch("change", { detail: { complete: this.complete(), pending: this.pending } })
  }

  complete() {
    const filled = (input) => Boolean(input && input.value.trim() !== "")
    if (!filled(this.lineOneTargetOrNull()) || !filled(this.cityTargetOrNull())) return false
    if (this.postalCodeRequired() && !filled(this.postalCodeTargetOrNull())) return false

    return !this.stateMissing()
  }

  countryChanged() {
    this.searchInCountry()
    this.syncPostalCode()
    this.announce()
  }

  // The typeahead keeps its search URL's own parameters (the session token among them) and adds
  // what was typed.
  searchInCountry() {
    const typeahead = this.hasSearchTarget ? this.searchTarget.querySelector("[data-controller~='async-typeahead']") : null
    if (!typeahead || !this.countryField) return

    const url = new URL(typeahead.dataset.asyncTypeaheadSearchUrlValue, window.location.origin)
    url.searchParams.set("region", this.countryField.value)
    typeahead.dataset.asyncTypeaheadSearchUrlValue = `${url.pathname}${url.search}`
  }

  syncPostalCode() {
    if (!this.hasPostalCodeTarget || this.optionalPostalCodeCountriesValue.length === 0) return

    this.postalCodeTarget.placeholder = this.postalCodeRequired() ? (this.postalCodeTarget.dataset.placeholder || "") : "Optional"
  }

  postalCodeRequired() {
    const country = (this.countryField?.value || "").toUpperCase()
    return !this.optionalPostalCodeCountriesValue.includes(country)
  }

  async fillFromSuggestion(event) {
    const placeId = event.detail?.placeId
    if (event.detail?.reason !== "select" || !placeId || !this.addressUrlValue) return

    this.pending = true
    this.announce()
    try {
      const url = new URL(`${this.addressUrlValue}/${encodeURIComponent(placeId)}`, window.location.origin)
      url.searchParams.set("session_token", this.sessionTokenValue)
      const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
      if (!response.ok) throw new Error(`address lookup ${response.status}`)

      this.applyAddress(await response.json())
    } catch {
      this.modeValue = "manual"
    } finally {
      this.pending = false
      this.announce()
    }
  }

  applyAddress(address) {
    if (address.country) this.chooseInSelect(this.countryField, address.country)
    this.setField(this.lineOneTargetOrNull(), address.address_line_one)
    this.setField(this.lineTwoTargetOrNull(), address.address_line_two)
    this.setField(this.cityTargetOrNull(), address.city)
    this.setField(this.postalCodeTargetOrNull(), address.postal_code)
    this.setState(address.state)

    if (!this.complete()) this.modeValue = "manual"
  }

  // Going to the fields, what was typed into the search becomes the first line if that is still
  // empty; going back, the search shows the address the fields now hold.
  carryOver(searching) {
    const query = this.hasSearchTarget ? this.searchTarget.querySelector("[data-async-typeahead-target='input']") : null
    const lineOne = this.lineOneTargetOrNull()
    if (!query || !lineOne) return

    if (!searching && lineOne.value.trim() === "" && query.value.trim() !== "") {
      this.setField(lineOne, query.value.trim())
    } else if (searching) {
      const parts = [lineOne, this.cityTargetOrNull(), this.postalCodeTargetOrNull()]
        .map((input) => input?.value.trim()).filter(Boolean)
      if (parts.length > 0) query.value = parts.join(", ")
    }
  }

  setField(input, value) {
    if (!input || input.readOnly || value === undefined || value === null) return

    input.value = value
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // The state is the enabled state field — a list for the US and Canada, free text elsewhere —
  // once the country has had its say.
  setState(value) {
    const field = this.activeStateField()
    if (!field || !value) return

    if (field.closest("[data-controller~='app-select']")) {
      this.chooseInSelect(field, value)
    } else {
      this.setField(field, value)
    }
  }

  // The app select reconciles its button to a value set on its field when the field reports an
  // input. Its own `app-select:change` is what the state list listens for.
  chooseInSelect(field, value) {
    if (!field || field.value === value) return

    field.value = value
    field.dispatchEvent(new Event("input", { bubbles: true }))
    field.dispatchEvent(new CustomEvent("app-select:change", { bubbles: true, detail: { value, reason: "select" } }))
  }

  countryInput() {
    return this.hasCountryTarget ? this.countryTarget.querySelector("[data-app-select-target='hidden'][name]") : null
  }

  activeStateField() {
    if (!this.stateNameValue) return null

    return Array.from(this.element.querySelectorAll(`[name='${this.stateNameValue}']`)).find((field) => !field.disabled)
  }

  stateMissing() {
    const field = this.activeStateField()
    return Boolean(field && field.closest("[data-controller~='app-select']") && field.value.trim() === "")
  }

  lineOneTargetOrNull() { return this.hasLineOneTarget ? this.lineOneTarget : null }
  lineTwoTargetOrNull() { return this.hasLineTwoTarget ? this.lineTwoTarget : null }
  cityTargetOrNull() { return this.hasCityTarget ? this.cityTarget : null }
  postalCodeTargetOrNull() { return this.hasPostalCodeTarget ? this.postalCodeTarget : null }
}
